import React from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

// Validation function (duplicated from HoursRecordView.js for calendar use)
// IMPORTANT: Keep in sync with HoursRecordView validation logic
const validateRestPattern = (periods) => {
  if (!periods || periods.length === 0) {
    return { valid: true }; // No periods = no violation to display
  }

  if (periods.length === 1) {
    return { valid: true };
  }

  if (periods.length > 2) {
    return {
      valid: false,
      error: `MGN 505 Requirement: Daily rest cannot be split into more than 2 periods.`
    };
  }

  if (periods.length === 2) {
    const durations = periods.map(period => {
      if (!period.start || !period.end) return 0;

      const startTime = new Date(`2000-01-01T${period.start}`);
      let endTime = new Date(`2000-01-01T${period.end}`);

      if (endTime <= startTime) {
        endTime = new Date(`2000-01-02T${period.end}`);
      }

      const hours = (endTime - startTime) / (1000 * 60 * 60);
      return hours;
    });

    const hasValidSixHourPeriod = durations.some(duration => duration >= 6);

    if (!hasValidSixHourPeriod) {
      return {
        valid: false,
        error: `MGN 505 Requirement: When rest is divided into 2 periods, one period must be at least 6 hours.`
      };
    }

    // Check 14-hour interval
    const period1Start = new Date(`2000-01-01T${periods[0].start}`);
    let period1End = new Date(`2000-01-01T${periods[0].end}`);

    if (period1End <= period1Start) {
      period1End = new Date(`2000-01-02T${periods[0].end}`);
    }

    let period2Start = new Date(`2000-01-01T${periods[1].start}`);

    if (period2Start <= period1End) {
      const period2StartRaw = new Date(`2000-01-01T${periods[1].start}`);

      if (period2StartRaw < period1End) {
        period2Start = new Date(period1End.getTime() + (24 * 60 * 60 * 1000));
        period2Start.setHours(period2StartRaw.getHours());
        period2Start.setMinutes(period2StartRaw.getMinutes());
      }
    }

    const intervalMillis = period2Start - period1End;
    const intervalHours = intervalMillis / (1000 * 60 * 60);

    if (intervalHours > 14) {
      return {
        valid: false,
        error: `MGN 505 Requirement: The interval between rest periods must not exceed 14 hours.`
      };
    }
  }

  return { valid: true };
};

// Helper: Get compliance status for a date
const getDateStatus = (date, hoursData, selectedCrew, getLocalDateString) => {
  const dateStr = getLocalDateString(date);

  // Check status across all selected crew
  let hasData = false;
  let hasViolation = false;
  let hasDeviation = false;
  let violationDetails = null;

  selectedCrew.forEach(crewId => {
    const record = hoursData[crewId]?.[dateStr];
    if (record) {
      hasData = true;

      // Check if record has deviation (indicates documented non-compliance)
      if (record.deviation) {
        hasDeviation = true;
      } else {
        // No deviation - validate the pattern
        if (record.periods && record.periods.length > 0) {
          const validation = validateRestPattern(record.periods);
          if (!validation.valid) {
            hasViolation = true;
            violationDetails = validation.error;
          }
        }
      }
    }
  });

  // Return status with priority: violation > deviation > compliant > empty
  if (!hasData) return { status: 'empty', details: null };
  if (hasViolation) return { status: 'violation', details: violationDetails };
  if (hasDeviation) return { status: 'deviation', details: null };
  return { status: 'compliant', details: null };
};

// Helper: Get tooltip text for a date
const getDateTooltip = (date, hoursData, selectedCrew, getLocalDateString) => {
  const dateStr = getLocalDateString(date);
  const { status, details } = getDateStatus(date, hoursData, selectedCrew, getLocalDateString);

  switch(status) {
    case 'empty':
      return 'No hours recorded for this date';

    case 'compliant':
      // Find a record to show total rest hours
      const record = selectedCrew
        .map(id => hoursData[id]?.[dateStr])
        .find(r => r);

      if (record && record.restHours !== undefined) {
        return `Valid rest - ${record.restHours.toFixed(1)} hours recorded`;
      }
      return 'Valid rest pattern';

    case 'violation':
      // Extract first line of error for tooltip
      if (details) {
        const firstLine = details.split('\n')[0];
        return `Invalid pattern - ${firstLine}`;
      }
      return 'Invalid rest pattern - click to view details';

    case 'deviation':
      // Find deviation reason
      const deviationRecord = selectedCrew
        .map(id => hoursData[id]?.[dateStr])
        .find(r => r?.deviation);

      if (deviationRecord?.deviation?.reason) {
        return `Documented exception - ${deviationRecord.deviation.reason}`;
      }
      return 'Documented exception';

    default:
      return '';
  }
};

const DateSelectionCalendar = ({ 
  selectedDates, 
  onToggleDate, 
  hoursData,
  selectedCrew,
  currentMonth,
  onMonthChange 
}) => {
  const getLocalDateString = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get calendar days
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add all days in month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const monthDays = getDaysInMonth(currentMonth);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Calendar className="h-5 w-5 mr-2 text-gray-500" />
            Select Dates
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-medium">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button
              onClick={() => onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      <div className="p-4">
        <div className="grid grid-cols-7 gap-1 text-center mb-2">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="text-xs font-medium text-gray-500 py-1">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {monthDays.map((day, index) => {
            if (!day) {
              return <div key={`empty-${index}`} className="aspect-square" />;
            }
            const dateStr = getLocalDateString(day);
            const isSelected = selectedDates.includes(dateStr);
            const isToday = dateStr === getLocalDateString();
            
            // Get compliance status for this date
            const { status } = getDateStatus(day, hoursData, selectedCrew, getLocalDateString);
            const tooltip = getDateTooltip(day, hoursData, selectedCrew, getLocalDateString);

            // Determine styling based on status
            const getStatusClasses = () => {
              if (isSelected) {
                return 'bg-cyan-600 text-white font-medium';
              }

              switch(status) {
                case 'violation':
                  return 'border-2 border-red-600 text-gray-900 hover:bg-red-50';
                case 'deviation':
                  return 'border-2 border-orange-500 text-gray-900 hover:bg-orange-50';
                case 'compliant':
                  return 'bg-blue-50 text-blue-700 hover:bg-blue-100';
                case 'empty':
                default:
                  return isToday
                    ? 'bg-cyan-50 text-cyan-600 font-medium hover:bg-cyan-100'
                    : 'hover:bg-gray-100 text-gray-700';
              }
            };

            // Render indicator based on status
            const renderIndicator = () => {
              if (isSelected || status === 'empty') return null;

              switch(status) {
                case 'compliant':
                  return (
                    <div className="absolute top-0.5 right-0.5 text-blue-600 text-lg leading-none font-bold">
                      •
                    </div>
                  );
                case 'violation':
                  return (
                    <div className="absolute top-0.5 right-0.5 text-red-600 text-xs font-bold leading-none">
                      !
                    </div>
                  );
                case 'deviation':
                  return (
                    <div className="absolute top-0.5 right-0.5 text-orange-500 text-xs leading-none">
                      ⚠
                    </div>
                  );
                default:
                  return null;
              }
            };

            return (
              <button
                key={dateStr}
                onClick={() => onToggleDate(day)}
                className={`
                  aspect-square flex items-center justify-center text-sm rounded-md relative
                  ${getStatusClasses()}
                `}
                title={tooltip}
              >
                {day.getDate()}
                {renderIndicator()}
              </button>
            );
          })}
        </div>
        {/* Calendar Legend - Updated for compliance status */}
        <div className="mt-4 space-y-2">
          <p className="text-xs font-medium text-gray-700 mb-2">Legend:</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 bg-cyan-600 rounded"></div>
              <span className="text-gray-600">Selected</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center text-blue-600 text-lg font-bold leading-none">•</div>
              <span className="text-gray-600">Compliant</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 border-2 border-red-600 rounded flex items-center justify-center text-red-600 text-xs font-bold">!</div>
              <span className="text-gray-600">Violation</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 border-2 border-orange-500 rounded flex items-center justify-center text-orange-500 text-xs">⚠</div>
              <span className="text-gray-600">Exception</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 bg-cyan-50 rounded"></div>
              <span className="text-gray-600">Today</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 border border-gray-300 rounded"></div>
              <span className="text-gray-600">No data</span>
            </div>
          </div>
        </div>
        
        {selectedDates.length > 0 && (
          <div className="mt-3 p-3 bg-cyan-50 rounded-lg">
            <p className="text-sm text-cyan-900">
              {selectedDates.length} day{selectedDates.length !== 1 ? 's' : ''} selected
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateSelectionCalendar;